// app/src/lib/ocr/invoiceTotals.js — the invoice total, broken into the parts
// that make it up.
//
// Why this exists: the review screen used to fold tax and freight into one
// header figure and show nothing else, so an invoice whose items visibly summed
// to $95.30 could display a $99.90 total with no way to see — let alone correct
// — where the extra $4.60 came from. Nothing was wrong arithmetically, which is
// exactly why no warning fired: the operator simply had no account of the
// number they were about to save.
//
// The rule here is that the total is ALWAYS the sum of parts the operator can
// see and edit:
//
//     total = items subtotal + tax + delivery/fees + other − discount
//
// So "let me change the total" can't mean "detach the total from its parts" —
// that invariant is also what db `recomputeInvoiceTotals()` re-derives after any
// later line edit, so a detached override would silently evaporate. Typing a
// total instead lands the difference in `other`, a visible line the operator
// can see, name to themselves, and undo.
//
// All amounts are integer cents.

/**
 * A line's extended (line) total: trust the printed extended when the document
 * gave us one, otherwise derive it from quantity × unit price less any per-line
 * discount. (When a printed extended exists it already reflects the discount,
 * so we must not subtract it twice.)
 *
 * This is THE definition of a line total — the review screen, the reconciler
 * and the totals block all call it, so they cannot drift apart.
 * @param {{ quantity?:number, unitPriceCents?:number, extPriceCents?:number|null, discountCents?:number }} line
 * @returns {number} cents
 */
export function lineExtendedCents(line) {
  if (!line) return 0;
  if (line.extPriceCents != null) return line.extPriceCents;
  const gross = Math.round((Number(line.quantity) || 0) * (Number(line.unitPriceCents) || 0));
  return Math.max(0, gross - (line.discountCents || 0));
}

/**
 * Break a draft's total into its visible parts.
 *
 * @param {{ lines?:Array, taxCents?:number, freightCents?:number, otherCents?:number, discountCents?:number, printedTotalCents?:number|null }} draft
 * @returns {{ subtotalCents:number, taxCents:number, freightCents:number,
 *             otherCents:number, discountCents:number, totalCents:number,
 *             printedTotalCents:number|null, matchesPrinted:boolean,
 *             printedDeltaCents:number }}
 */
export function invoiceTotals(draft) {
  const d = draft || {};
  const lines = d.lines || [];
  const subtotalCents = lines.reduce((s, l) => s + lineExtendedCents(l), 0);
  const taxCents = d.taxCents || 0;
  const freightCents = d.freightCents || 0;
  const otherCents = d.otherCents || 0;
  const discountCents = d.discountCents || 0;
  const totalCents = subtotalCents + taxCents + freightCents + otherCents - discountCents;
  const printedTotalCents = d.printedTotalCents != null ? d.printedTotalCents : null;
  const printedDeltaCents = printedTotalCents == null ? 0 : totalCents - printedTotalCents;
  return {
    subtotalCents, taxCents, freightCents, otherCents, discountCents, totalCents,
    printedTotalCents,
    matchesPrinted: printedTotalCents == null || printedDeltaCents === 0,
    printedDeltaCents,
  };
}

/**
 * What to change so the draft's total becomes `desiredTotalCents`.
 *
 * The difference lands in `other` rather than detaching the total from its
 * parts: a detached total would be re-derived away by recomputeInvoiceTotals()
 * the next time any line is edited, so the operator's correction would vanish
 * without a trace. `other` may be negative — that is a credit, and the review
 * screen shows it as one.
 *
 * @param {object} draft
 * @param {number} desiredTotalCents
 * @returns {{ otherCents:number }} a patch to merge into the draft
 */
export function totalOverridePatch(draft, desiredTotalCents) {
  const { totalCents, otherCents } = invoiceTotals(draft);
  const want = Math.round(Number(desiredTotalCents) || 0);
  return { otherCents: otherCents + (want - totalCents) };
}

/**
 * Does the total the operator is about to save still agree with the total
 * PRINTED on the document? Disagreeing isn't necessarily wrong — they may have
 * deliberately corrected a misread — but it must never pass silently.
 *
 * `toleranceCents` absorbs per-line cent rounding across the computed lines.
 * @returns {{ known:boolean, agrees:boolean, deltaCents:number }}
 */
export function agreesWithPrinted(draft, toleranceCents) {
  const { totalCents, printedTotalCents } = invoiceTotals(draft);
  if (printedTotalCents == null) return { known: false, agrees: true, deltaCents: 0 };
  const tol = toleranceCents ?? Math.max(2, ((draft && draft.lines) || []).length);
  const deltaCents = totalCents - printedTotalCents;
  return { known: true, agrees: Math.abs(deltaCents) <= tol, deltaCents };
}
